'use client';

// backwards compatible router/navigation hooks. These are meant to be used in either page or app router apps and behave the same.

import { encode } from 'querystring';
// eslint-disable-next-line @alltrails/custom/client-navigation
import { useRouter as useAppRouter, usePathname, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import usePageRouter from './useRouter';

export function useClientNavigationSearchParams() {
  const pageRouter = usePageRouter();
  const appRouterSearchParams = useSearchParams();

  const pageRouterQuery = pageRouter?.query;
  const isPageRouter = Boolean(pageRouter);

  // Cache the result so we don't continually generate a new and unique UrlSearchParams object.
  return useMemo(() => {
    if (isPageRouter) {
      return new URLSearchParams(encode(pageRouterQuery));
    }

    return appRouterSearchParams;
  }, [isPageRouter, pageRouterQuery, appRouterSearchParams]);
}

export function useClientNavigationPathname() {
  const pageRouter = usePageRouter();
  const appRouterPathName = usePathname();

  if (pageRouter) {
    return pageRouter.asPath;
  }

  return appRouterPathName;
}

// useClientNavigationPushState is meant to abstract page and app router "shallow" pushes. Pure client-side local navigation.
export function useClientNavigationPushState() {
  const pageRouter = usePageRouter();
  const appRouter = useAppRouter();

  if (pageRouter) {
    return href => pageRouter.push(href, undefined, { shallow: true });
  }

  // Next.js has both router.push and the history pushState API. It's unclear how they vary for client-side pushes.
  //
  // > The useRouter hook allows you to programmatically change routes inside Client Components.
  // > - https://nextjs.org/docs/app/api-reference/functions/use-router
  //
  // > Next.js allows you to use the native window.history.pushState and window.history.replaceState methods to update the browser's history stack without reloading the page.
  // > pushState and replaceState calls integrate into the Next.js Router, allowing you to sync with usePathname and useSearchParams.
  // > - https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#using-the-native-history-api
  //
  // For now, use the push() API from the app router, but this may change in the future.
  return href => appRouter.push(href, { scroll: false });
}
